import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { DateRangePicker } from '../../components/ui/DateRangePicker';
import { ProjectSelector } from '../../components/projects/ProjectSelector';
import { AnalyticsOverview } from '../../components/analytics/AnalyticsOverview';
import { TrafficAnalysis } from '../../components/analytics/TrafficAnalysis';
import { ConversionAnalysis } from '../../components/analytics/ConversionAnalysis';
import { AudienceAnalysis } from '../../components/analytics/AudienceAnalysis';
import { RealtimeAnalytics } from '../../components/analytics/RealtimeAnalytics';
import { 
  BarChart3, 
  Users, 
  Target, 
  TrendingUp, 
  Zap,
  Download,
  Sparkles,
  MessageSquare
} from 'lucide-react';

export function AnalyticsPage() {
  const { projectId } = useParams();
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date()
  });
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Comprehensive analytics dashboard for your marketing data
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <ProjectSelector />
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
          />
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
            <MessageSquare className="w-4 h-4 mr-2" />
            Ask AI Agent
          </Button>
        </div>
      </div>

      {/* Real-time Banner */}
      <Card className="p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-green-200 dark:border-green-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center mr-3">
              <Zap className="w-4 h-4 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Real-time Data</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">42 active users on your site right now</p>
            </div>
          </div>
          <Button variant="outline" size="sm">
            View Real-time
          </Button>
        </div>
      </Card>

      {/* Analytics Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview" className="flex items-center">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="traffic" className="flex items-center">
            <TrendingUp className="w-4 h-4 mr-2" />
            Traffic
          </TabsTrigger>
          <TabsTrigger value="conversions" className="flex items-center">
            <Target className="w-4 h-4 mr-2" />
            Conversions
          </TabsTrigger>
          <TabsTrigger value="audience" className="flex items-center">
            <Users className="w-4 h-4 mr-2" />
            Audience
          </TabsTrigger>
          <TabsTrigger value="realtime" className="flex items-center">
            <Zap className="w-4 h-4 mr-2" />
            Real-time
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <AnalyticsOverview dateRange={dateRange} projectId={projectId} />
        </TabsContent>

        <TabsContent value="traffic" className="space-y-6">
          <TrafficAnalysis dateRange={dateRange} projectId={projectId} />
        </TabsContent>

        <TabsContent value="conversions" className="space-y-6">
          <ConversionAnalysis dateRange={dateRange} projectId={projectId} />
        </TabsContent>

        <TabsContent value="audience" className="space-y-6">
          <AudienceAnalysis dateRange={dateRange} projectId={projectId} />
        </TabsContent>

        <TabsContent value="realtime" className="space-y-6">
          <RealtimeAnalytics projectId={projectId} />
        </TabsContent>
      </Tabs>

      {/* AI Insights Panel */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-blue-200 dark:border-blue-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">AI Insights</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Your mobile traffic increased 34% this week. Consider optimizing mobile experience.
              </p>
            </div>
          </div>
          <Button variant="outline">
            View Analysis
          </Button>
        </div>
      </Card>
    </div>
  );
}